import React, { useEffect, useState, useRef } from "react";
import { Controller, Control, FieldErrors, UseFormSetValue } from "react-hook-form";
import { Form } from "react-bootstrap";
import { format, subMonths } from "date-fns";
import { DateRangePicker, RangeKeyDict } from "react-date-range";
import { useMediaQuery } from "../../hooks/useMediaQuery";

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface DateRangePickerComponentProps {
    control: Control<any>;
    errors: FieldErrors<{
        filter: {
            property_id: string;
            start_date: string;
            end_date: string;
            room_type_id?: string;
        };
    }>;
    setValue: UseFormSetValue<any>;
}

const DateRangePickerComponent: React.FC<DateRangePickerComponentProps> = ({ control, errors, setValue }) => {
    const today = new Date();
    const firstDayOfLastMonth = subMonths(today, 1);

    const isMobile = useMediaQuery('(max-width: 768px)');
    const calendarRef = useRef<HTMLDivElement>(null);

    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [dateRange, setDateRange] = useState([
        {
            startDate: firstDayOfLastMonth,
            endDate: today,
            key: 'selection'
        }
    ]);

    const formatDate = (date: Date) => format(date, "yyyy-MM-dd");

    useEffect(() => {
        setValue("filter.start_date", formatDate(firstDayOfLastMonth));
        setValue("filter.end_date", formatDate(today));
    }, [setValue]);

    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (selection.startDate && selection.endDate) {
            setDateRange([{
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: selection.key || 'selection'
            }]);
            setValue('filter.start_date', formatDate(selection.startDate));
            setValue('filter.end_date', formatDate(selection.endDate));
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [calendarRef]);

    const calendarContainerStyle: React.CSSProperties = {
        position: 'absolute',
        zIndex: 1000,
        top: '100%',
        marginTop: '8px',
        border: '1px solid #dfe2e5',
        borderRadius: '8px',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
    };

    if (isMobile) {
        calendarContainerStyle.left = '50%';
        calendarContainerStyle.transform = 'translateX(-50%)';
    } else {
        calendarContainerStyle.right = 0;
    }

    return (
        <div>
            <Form.Label>Select Range:</Form.Label>
            <div className="position-relative" ref={calendarRef}>
                <Controller
                    name="filter.start_date"
                    control={control}
                    render={() => ( // The 'field' object has been removed here
                        <div className="input-group">
                            <input
                                type='text'
                                readOnly
                                className='form-control'
                                onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                                value={`${format(dateRange[0].startDate, 'dd MMM yyyy')} - ${format(dateRange[0].endDate, 'dd MMM yyyy')}`}
                                placeholder="Select date range"
                            />
                            <span className="input-group-text" onClick={() => setIsCalendarOpen(!isCalendarOpen)} style={{ cursor: 'pointer' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                                    <path d="M16 3l0 4"></path>
                                    <path d="M8 3l0 4"></path>
                                    <path d="M4 11l16 0"></path>
                                    <path d="M8 15h2v2h-2z"></path>
                                </svg>
                            </span>
                        </div>
                    )}
                />
                {isCalendarOpen && (
                    <div style={calendarContainerStyle}>
                        <DateRangePicker
                            onChange={handleDateChange}
                            moveRangeOnFirstSelection={false}
                            ranges={dateRange}
                            months={isMobile ? 1 : 2}
                            direction={isMobile ? "vertical" : "horizontal"}
                            inputRanges={[]}
                        />
                    </div>
                )}
                {errors?.filter?.start_date && (
                    <Form.Text className="text-danger">
                        {errors.filter.start_date.message as string}
                    </Form.Text>
                )}
                {errors?.filter?.end_date && (
                    <Form.Text className="text-danger">
                        {errors.filter.end_date.message as string}
                    </Form.Text>
                )}
            </div>
        </div>
    );
};

export default DateRangePickerComponent;